import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { depenses } from '../services/api';
import { useNdf } from '../NdfContext';
import { eur } from '../services/format';
import Visionneuse from './Visionneuse';
import SelecteurDossier from './SelecteurDossier';
import './Fiche.css';

// Le HT est une déduction comptable, pas une saisie : HT = TTC - somme des
// TVA (identité vraie même en TVA partielle — la part non soumise reste dans
// le HT). Il se recalcule donc tout seul, sauf si l'utilisateur l'a édité à
// la main ; le bouton ↻ le réaligne et réarme l'automatique.
function versNombre(v) {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
function htCalcule(c) {
  const ttc = versNombre(c.montant_ttc);
  if (ttc === null) return null;
  const tva = c.tva.reduce((somme, t) => somme + (versNombre(t.montant) || 0), 0);
  return Math.round((ttc - tva) * 100) / 100;
}

// La fiche d'une dépense : le justificatif d'un côté, les champs pré-remplis
// par l'IA de l'autre. L'utilisateur corrige (rarement) et enregistre. Une
// dépense déjà soumise est en lecture seule.
function FicheDepense() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refs } = useNdf();
  const [dep, setDep] = useState(null);
  const [url, setUrl] = useState(null);
  const [champs, setChamps] = useState(null);
  const [erreur, setErreur] = useState('');
  const [sauvegarde, setSauvegarde] = useState(false);
  const [apercu, setApercu] = useState(false);
  const [htAuto, setHtAuto] = useState(true);
  const initFait = useRef(false);

  const charger = useCallback(async () => {
    try {
      const res = await depenses.lire(id);
      setDep(res.data.depense);
      setUrl(res.data.justificatif_url);
      return res.data.depense;
    } catch (err) {
      setErreur(err.response?.data?.error || 'Dépense introuvable');
      return null;
    }
  }, [id]);

  useEffect(() => { charger(); }, [charger]);

  // Pendant l'analyse IA, on sonde toutes les 2 s ; à la fin, les champs
  // arrivent pré-remplis.
  const enAnalyse = dep?.statut_analyse === 'analyse';
  useEffect(() => {
    if (!enAnalyse) return undefined;
    const t = setInterval(charger, 2000);
    return () => clearInterval(t);
  }, [enAnalyse, charger]);

  // Les champs éditables sont initialisés (ou réinitialisés) quand l'analyse
  // se termine — pas à chaque poll. Le recalcul auto du HT démarre armé si le
  // HT est vide ou déjà cohérent avec TTC - TVA.
  useEffect(() => {
    if (!dep || dep.statut_analyse === 'analyse' || initFait.current) return;
    initFait.current = true;
    const init = {
      date_depense: dep.date_depense ? dep.date_depense.slice(0, 10) : '',
      marchand: dep.marchand || '',
      categorie_id: dep.categorie_id || null,
      dossier_id: dep.dossier_id || null,
      montant_ttc: dep.montant_ttc !== null ? String(dep.montant_ttc) : '',
      montant_ht: dep.montant_ht !== null ? String(dep.montant_ht) : '',
      tva: Array.isArray(dep.tva_details) ? dep.tva_details.map((t) => ({ taux: t.taux ?? '', montant: t.montant ?? '' })) : [],
      moyen_paiement: dep.moyen_paiement || 'perso',
      commentaire: dep.commentaire || '',
      distance_km: dep.distance_km !== null && dep.distance_km !== undefined ? String(dep.distance_km) : '',
      motif_deplacement: dep.motif_deplacement || '',
    };
    const calc = htCalcule(init);
    setHtAuto(init.montant_ht === '' || (calc !== null && Math.abs((versNombre(init.montant_ht) ?? 0) - calc) <= 0.01));
    setChamps(init);
  }, [dep]);

  if (erreur && !dep) return <div className="nf-erreur-bandeau">{erreur}</div>;
  if (!dep) return null;

  const lectureSeule = dep.note_id !== null;
  const estKm = dep.type === 'km';
  const maj = (k, v) => setChamps((c) => ({ ...c, [k]: v }));
  // Toute modification de TTC ou de TVA ré-aligne le HT si l'auto est armé.
  const majAvecHt = (transforme) => setChamps((c) => {
    const n = transforme(c);
    if (htAuto) {
      const calc = htCalcule(n);
      if (calc !== null) n.montant_ht = calc.toFixed(2);
    }
    return n;
  });

  const enregistrer = async () => {
    setErreur('');
    setSauvegarde(true);
    try {
      const payload = estKm
        ? {
            dossier_id: champs.dossier_id,
            date_depense: champs.date_depense || null,
            distance_km: champs.distance_km ? parseFloat(champs.distance_km) : null,
            motif_deplacement: champs.motif_deplacement,
            commentaire: champs.commentaire,
          }
        : {
            dossier_id: champs.dossier_id,
            date_depense: champs.date_depense || null,
            marchand: champs.marchand,
            categorie_id: champs.categorie_id,
            montant_ttc: champs.montant_ttc === '' ? null : parseFloat(String(champs.montant_ttc).replace(',', '.')),
            montant_ht: champs.montant_ht === '' ? null : parseFloat(String(champs.montant_ht).replace(',', '.')),
            tva_details: champs.tva
              .map((t) => ({ taux: t.taux === '' ? null : parseFloat(String(t.taux).replace(',', '.')), montant: t.montant === '' ? null : parseFloat(String(t.montant).replace(',', '.')) }))
              .filter((t) => t.montant !== null),
            moyen_paiement: champs.moyen_paiement,
            commentaire: champs.commentaire,
          };
      await depenses.modifier(id, payload);
      navigate('/');
    } catch (err) {
      setErreur(err.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setSauvegarde(false);
    }
  };

  const supprimer = async () => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await depenses.supprimer(id);
      navigate('/');
    } catch (err) {
      setErreur(err.response?.data?.error || 'Suppression impossible');
    }
  };

  const reanalyser = async () => {
    setErreur('');
    try {
      await depenses.reanalyser(id);
      initFait.current = false;
      setChamps(null);
      charger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Réanalyse impossible');
    }
  };

  const estPdf = (dep.justificatif_type || '').includes('pdf');

  return (
    <div className="fd-page">
      <button type="button" className="fd-retour" onClick={() => navigate(-1)}>← Retour</button>

      {dep.statut_ligne === 'refusee' && dep.motif_refus && (
        <div className="nf-erreur-bandeau">Ligne écartée par votre valideur : {dep.motif_refus} — corrigez puis re-soumettez.</div>
      )}
      {erreur && <div className="nf-erreur-bandeau">{erreur}</div>}

      <div className="fd-colonnes">
        {url && (
          <div className="fd-justificatif">
            {estPdf ? (
              <button type="button" className="nf-btn" onClick={() => setApercu(true)}>📄 Voir le PDF</button>
            ) : (
              <button type="button" className="fd-image-bouton" onClick={() => setApercu(true)} aria-label="Agrandir le justificatif">
                <img src={url} alt="Justificatif" className="fd-image" />
              </button>
            )}
          </div>
        )}
        {apercu && (
          <Visionneuse url={url} type={dep.justificatif_type} titre={dep.marchand || 'Justificatif'} onClose={() => setApercu(false)} />
        )}

        <div className="fd-form">
          {enAnalyse ? (
            <div className="fd-analyse">
              <span className="md-spinner" />
              <div>
                <strong>Lecture du justificatif…</strong>
                <p>Montant, TVA, date et catégorie arrivent dans quelques secondes.</p>
              </div>
            </div>
          ) : champs && (
            <>
              {dep.statut_analyse === 'erreur' && (
                <div className="fd-bandeau-erreur-ia">
                  La lecture automatique a échoué — saisissez les champs à la main
                  {dep.justificatif_key && !lectureSeule && (
                    <button type="button" className="nf-btn" onClick={reanalyser}>Réessayer l'analyse</button>
                  )}
                </div>
              )}
              {dep.confiance && dep.statut_analyse !== 'erreur' && !dep.edite_manuellement && (
                <div className={`fd-confiance fd-confiance--${dep.confiance}`}>
                  {dep.confiance === 'haute' ? '✓ Lecture fiable (les montants bouclent)' :
                   dep.confiance === 'moyenne' ? 'Lecture partielle — vérifiez les champs' :
                   'Lecture incertaine — vérifiez tout'}
                </div>
              )}

              <label className="fd-champ">
                <span>Date</span>
                <input type="date" value={champs.date_depense} disabled={lectureSeule}
                  onChange={(e) => maj('date_depense', e.target.value)} />
              </label>

              {estKm ? (
                <>
                  <label className="fd-champ">
                    <span>Distance (km)</span>
                    <input type="number" inputMode="decimal" value={champs.distance_km} disabled={lectureSeule}
                      onChange={(e) => maj('distance_km', e.target.value)} />
                  </label>
                  <label className="fd-champ">
                    <span>Motif du déplacement</span>
                    <input type="text" value={champs.motif_deplacement} disabled={lectureSeule}
                      onChange={(e) => maj('motif_deplacement', e.target.value)} />
                  </label>
                  <div className="fd-km-resume">
                    {dep.puissance} · taux {dep.taux_applique} €/km · <strong className="nf-montant">{eur(dep.montant_ttc || 0)}</strong>
                    {champs.distance_km && parseFloat(champs.distance_km) !== parseFloat(dep.distance_km) &&
                      <span> → {eur(parseFloat(champs.distance_km || 0) * parseFloat(dep.taux_applique))} après enregistrement</span>}
                  </div>
                </>
              ) : (
                <>
                  <label className="fd-champ">
                    <span>Marchand</span>
                    <div className="fd-effacable">
                      <input type="text" value={champs.marchand} disabled={lectureSeule}
                        onChange={(e) => maj('marchand', e.target.value.toUpperCase())} placeholder="Nom du commerçant" />
                      {!lectureSeule && champs.marchand && (
                        <button type="button" className="fd-effacer" aria-label="Effacer le marchand"
                          onClick={() => maj('marchand', '')}>✕</button>
                      )}
                    </div>
                  </label>

                  <div className="fd-champ">
                    <span>Catégorie</span>
                    <div className="fd-chips">
                      {refs.categories.filter((c) => !c.special).map((c) => (
                        <button key={c.id} type="button" disabled={lectureSeule}
                          className={`fd-chip ${champs.categorie_id === c.id ? 'active' : ''}`}
                          onClick={() => maj('categorie_id', c.id)}>
                          {c.icone} {c.libelle}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="fd-montants">
                    <label className="fd-champ">
                      <span>Total TTC</span>
                      <div className="fd-avec-suffixe">
                        <input type="text" inputMode="decimal" value={champs.montant_ttc} disabled={lectureSeule}
                          onChange={(e) => majAvecHt((c) => ({ ...c, montant_ttc: e.target.value }))} />
                        <span className="fd-suffixe" aria-hidden="true">€</span>
                      </div>
                    </label>
                    <label className="fd-champ">
                      <span>Total HT</span>
                      <div className="fd-avec-suffixe">
                        <input type="text" inputMode="decimal" value={champs.montant_ht} disabled={lectureSeule}
                          onChange={(e) => { setHtAuto(false); maj('montant_ht', e.target.value); }} />
                        {(() => {
                          const calc = htCalcule(champs);
                          const ecart = calc !== null && Math.abs((versNombre(champs.montant_ht) ?? 0) - calc) > 0.01;
                          return !lectureSeule && ecart ? (
                            <button type="button" className="fd-recalc"
                              title={`Recalculer : ${calc.toFixed(2)} € (TTC − TVA)`}
                              aria-label="Recalculer le HT depuis TTC et TVA"
                              onClick={() => { setHtAuto(true); maj('montant_ht', calc.toFixed(2)); }}>↻</button>
                          ) : null;
                        })()}
                        <span className="fd-suffixe" aria-hidden="true">€</span>
                      </div>
                    </label>
                  </div>

                  <div className="fd-champ">
                    <span>TVA</span>
                    {champs.tva.map((t, i) => (
                      <div key={i} className="fd-tva-ligne">
                        <div className="fd-avec-suffixe">
                          <input type="text" inputMode="decimal" value={t.taux} placeholder="taux" disabled={lectureSeule}
                            onChange={(e) => maj('tva', champs.tva.map((x, j) => j === i ? { ...x, taux: e.target.value } : x))} />
                          <span className="fd-suffixe" aria-hidden="true">%</span>
                        </div>
                        <div className="fd-avec-suffixe">
                          <input type="text" inputMode="decimal" value={t.montant} placeholder="montant" disabled={lectureSeule}
                            onChange={(e) => majAvecHt((c) => ({ ...c, tva: c.tva.map((x, j) => j === i ? { ...x, montant: e.target.value } : x) }))} />
                          <span className="fd-suffixe" aria-hidden="true">€</span>
                        </div>
                        {!lectureSeule && (
                          <button type="button" className="fd-tva-suppr" aria-label="Retirer cette ligne de TVA"
                            onClick={() => majAvecHt((c) => ({ ...c, tva: c.tva.filter((_, j) => j !== i) }))}>✕</button>
                        )}
                      </div>
                    ))}
                    {!lectureSeule && (
                      <button type="button" className="fd-tva-ajout"
                        onClick={() => maj('tva', [...champs.tva, { taux: '', montant: '' }])}>+ Ajouter un taux de TVA</button>
                    )}
                  </div>

                  <div className="fd-champ">
                    <span>Payé avec</span>
                    <div className="fd-chips">
                      <button type="button" disabled={lectureSeule}
                        className={`fd-chip ${champs.moyen_paiement === 'perso' ? 'active' : ''}`}
                        onClick={() => maj('moyen_paiement', 'perso')}>💳 Mes deniers (à rembourser)</button>
                      <button type="button" disabled={lectureSeule}
                        className={`fd-chip ${champs.moyen_paiement === 'carte_societe' ? 'active' : ''}`}
                        onClick={() => maj('moyen_paiement', 'carte_societe')}>🏢 Carte société</button>
                    </div>
                  </div>
                </>
              )}

              <SelecteurDossier valeur={champs.dossier_id} disabled={lectureSeule}
                onChange={(v) => maj('dossier_id', v)} />

              <label className="fd-champ">
                <span>Commentaire</span>
                <input type="text" value={champs.commentaire} disabled={lectureSeule}
                  onChange={(e) => maj('commentaire', e.target.value)} placeholder="facultatif" />
              </label>

              {!lectureSeule && (
                <div className="fd-actions">
                  <button type="button" className="nf-btn nf-btn--danger" onClick={supprimer}>Supprimer</button>
                  <button type="button" className="nf-btn nf-btn--principal fd-enregistrer" onClick={enregistrer} disabled={sauvegarde}>
                    {sauvegarde ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              )}
              {lectureSeule && <p className="fd-lecture-seule">Cette dépense fait partie d'une note soumise — lecture seule.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FicheDepense;
